define((require) => {
    "use strict";

    const _                           = require("underscore");
    const FontLoader                  = require("shared/util/FontLoader");
    const FontProvider                = require("shared/data/fonts/FontProvider");
    const FontsDotComApi              = require("editor/services/fonts/FontsDotComApi");
    const FontsDotComIntegrationModel = require("editor/ui/settings/integrations/FontsDotComIntegrationModel");
    const GoogleFontsApi              = require("editor/services/fonts/GoogleFontsApi");
    const LoggedInUserState           = require("shared/ui/user/LoggedInUserState");
    const {Logger}                    = require("shared/util/Logger");
    const Marionette                  = require("marionette");
    const Promise                     = require("shared/util/Promise");
    const StandardWebFontList         = require("shared/ui/assets/StandardWebFontList");
    const Storage                     = require("shared/services/Storage");
    const SyncManager                 = require("shared/services/sync/SyncManager");
    const SystemMessages              = require("editor/services/SystemMessages");
    const TypekitApi                  = require("editor/services/fonts/TypekitApi");
    const CustomFontCollection        = require("editor/services/fonts/customFonts/CustomFontCollection");
    const CustomFontIntegrationModel  = require("editor/ui/settings/integrations/CustomFontIntegrationModel");
    const TypekitIntegrationModel     = require("editor/ui/settings/integrations/TypekitIntegrationModel");

    const logger = new Logger("EditorWebFontController");

    /**
     * Map used for parsing the different font API responses.
     *
     * @type {Map.<FontProvider, Function>}
     */
    const parsers = new Map();

    // Custom
    parsers.set(FontProvider.CUSTOM, (customFontCollection) => (
        customFontCollection.map((customFontModel) => {
            const data = {
                path: customFontModel.get("path"),
                customFontId: customFontModel.get("id"),
                format: customFontModel.get("format")
            };

            const fontFamily = customFontModel.get("fontFamily");

            return {
                provider: FontProvider.CUSTOM,
                name: fontFamily,
                cssFamily: fontFamily,
                family: fontFamily,
                variations: ["400"],
                data
            };
        })
    ));

    // Google
    parsers.set(FontProvider.GOOGLE, (response) => {
        return _.map(response.items, (value) => {
            return {
                provider: FontProvider.GOOGLE,
                name: value.family,
                family: value.family,
                cssFamily: value.family,
                variations: value.variants
            };
        });
    });

    // Typekit
    parsers.set(FontProvider.TYPEKIT, (response) => {
        const descriptors = [];
        _.each(response, (item) => {
            const kit = item.kit;
            _.each(kit.families, (familiy) => {
                descriptors.push({
                    provider: FontProvider.TYPEKIT,
                    name: familiy.name,
                    family: familiy.name,
                    variations: familiy.variations,
                    cssFamily: familiy.css_names.toString(),
                    data: {
                        kitID: kit.id,
                        cssFamilyNames: familiy.css_names
                    }
                });
            });
        });

        return descriptors;
    });

    // FontsDotCom
    parsers.set(FontProvider.FONTS_DOT_COM, (fontProjects) => {
        const descriptors = [];
        _.each(fontProjects, (fontProject) => {
            _.each(fontProject.fonts, (font) => {
                // Variations of fonts from Fonts.com show up as individual fonts.
                // -> family and cssFamily is the same.
                // -> variations is always just "400", i.e. normal weight and not italic.
                descriptors.push({
                    provider: FontProvider.FONTS_DOT_COM,
                    name: font.name,
                    family: font.cssFamily,
                    cssFamily: font.cssFamily,
                    variations: [
                        "400"
                    ],
                    data: {
                        projectId: fontProject.projectId,
                        projectName: fontProject.name,
                        cssFamilyNames: font.cssFamily
                    }
                });
            });
        });

        const compareNames = (fontDescriptorA, fontDescriptorB) => {
            if (fontDescriptorA.name < fontDescriptorB.name) {
                return -1;
            }

            if (fontDescriptorA.name > fontDescriptorB.name) {
                return 1;
            }

            return 0;
        };

        descriptors.sort(compareNames);

        return descriptors;
    });

    /**
     * Responsible for setting up the initial fetching of fonts - Google Fonts and Typekit (if supported).
     * Also acts as the caching layer, exposing the user's fonts via `EditorWebFontController.collection`.
     *
     * Use `EditorWebFontController.promise` to access the collection.
     *
     * Google Fonts are sorted by most popular.
     *
     * The `EditorWebFontController` is a singleton.
     *
     * @class EditorWebFontController
     * @author Kim
     */
    const EditorWebFontController = Marionette.Controller.extend({

        /**
         * @type {FontDescriptorCollection}
         * The loaded font descriptors.
         */
        _collection: null,
        /**
         * @private @type {Promise}
         * A promise that will resolve once all fonts have been loaded, at which point `_collection`
         * will be populated with the loaded font descriptors.
         */
        _promise: null,

        /**
         * Starts the controller.
         *
         * If all global data is available (`ready`), we start fetching the font data immediately.
         */
        start() {
            if (LoggedInUserState.get("ready") && !this._promise) {
                this._startFetching();
            }
            this._listenToReadyEvent();
        },

        /**
         * Clears/resets the contained collection and removes all event listeners.
         */
        clear() {
            this._collection.reset();
            this.stopListening();
            this._promise = null;
            StandardWebFontList.reset();
        },

        /**
         * Returns a promise that resolves once the font collection has been successfully initialized
         * and ready to use.
         *
         * @return {Promise} A `Promise` instance; never null.
         */
        promise() {
            return this._promise;
        },

        /**
         * Removes all the Typekit Font descriptors (if any) and reloads font styles.
         * @param  {String} token The Typekit API token.
         */
        reloadKits(token) {
            this._collection.removeByProvider(FontProvider.TYPEKIT);
            this._fetchTypekit(token).done((response) => {
                logger.info("Adding Typekit fonts");
                const parser = parsers.get(FontProvider.TYPEKIT);

                // Add the fonts to the collection
                this._collection.add(parser(response));

                // Load the actual font styles
                FontLoader.fromFontDescriptors(this._collection.fromProvider(FontProvider.TYPEKIT));
            });
        },

        /**
         * Removes all Fonts.com Font descriptors (if any) and reloads font styles.
         */
        reloadFonsDotComFonts() {
            this._collection.removeByProvider(FontProvider.FONTS_DOT_COM);
            this._fetchFontsDotCom(FontsDotComApi).done((response) => {
                logger.info("Adding Fonts.com fonts");
                const parser = parsers.get(FontProvider.FONTS_DOT_COM);
                this._collection.add(parser(response));
                FontLoader.fromFontDescriptors(this._collection.fromProvider(FontProvider.FONTS_DOT_COM));
            });
        },

        _readyChangeHandler() {
            if (LoggedInUserState.get("ready")) { // ready: logged in *and* user data ready
                this._startFetching();
            } else {
                // Reset state.
                this.clear();
                this._listenToReadyEvent();
            }
        },

        /**
         * Registers a listener for the `ready` event on the `LoggedInUserState`.
         */
        _listenToReadyEvent() {
            this.listenTo(LoggedInUserState, "change:ready", this._readyChangeHandler);
        },

        /**
         * @private
         */
        _startFetching() {
            const integrations = LoggedInUserState.get("integrations");

            // Initialize the collection of fonts with the standard web fonts, which will be resolved once
            // the queue completes.
            this._collection = StandardWebFontList.collection();

            // Create a parallel queue to fetch the fonts, resolving font descriptors, and adding them to the
            // resolved font collection when the queue completes.
            const queue = SyncManager.queue(true, (result, request, provider) => { // "true" -> parallel
                // Get parser for provider and parse "result" into usable font descriptors to store in the
                // font collection eventually resolved.
                this._collection.add(parsers.get(provider)(result));
            });

            // Suppress error notifications here, already handled by the font loader itself
            const errorHandler = () => false;

            logger.info("Start loading font data");

            // Enqueue a request to load the Google fonts via the Google Font API.
            queue.enqueue({
                method: `${FontProvider.GOOGLE.value}:descriptors`,
                fn: () => GoogleFontsApi.mostPopular(),
                errorHandler
            }, FontProvider.GOOGLE);

            // If font integrations are enabled, fetch additional fonts
            if (integrations.fontIntegrationsEnabled()) {
                // Custom Fonts integration
                const customFontsIntegration = integrations.getIntegration(CustomFontIntegrationModel);

                if (customFontsIntegration) {
                    // Enqueue a request to load the any custom fonts.
                    queue.enqueue({
                        method: `${FontProvider.CUSTOM.value}:descriptors`,
                        fn: () => new CustomFontCollection().fetch(),
                        errorHandler
                    }, FontProvider.CUSTOM);
                }

                // Typekit Integration
                const typekitIntegration = integrations.getIntegration(TypekitIntegrationModel);

                if (typekitIntegration && typekitIntegration.has("typekitApiToken")) {
                    const typeKit = new TypekitApi(typekitIntegration.get("typekitApiToken"));
                    // Enqueue a request to load the Typekit fonts via the Typekit API.
                    queue.enqueue({
                        method: `${FontProvider.TYPEKIT.value}:descriptors`,
                        fn: () => this._fetchTypekit(typeKit),
                        errorHandler
                    }, FontProvider.TYPEKIT);
                }

                // Fonts.com integration
                const fontsDotComIntegration = integrations.getIntegration(FontsDotComIntegrationModel);

                if (fontsDotComIntegration && fontsDotComIntegration.has("authenticationKey")) {
                    // Enqueue a request to load the Fonts.com font families using the Fonts.com api client.
                    queue.enqueue({
                        method: `${FontProvider.FONTS_DOT_COM.value}:descriptors`,
                        fn: () => this._fetchFontsDotCom(),
                        errorHandler
                    }, FontProvider.FONTS_DOT_COM);
                }
            } else {
                this.clearFontType(FontProvider.CUSTOM);
                this.clearFontType(FontProvider.TYPEKIT);
                this.clearFontType(FontProvider.FONTS_DOT_COM);
            }

            // Get and set promise for when the queue has completed, i.e. loaded fonts.
            this._promise = queue.sync().then(() => {
                this.trigger("fontsLoaded");
                logger.info("Font data loaded");
                this._loadCssOfInitialFonts();
                return this._collection;
            }).fail(() => {
                logger.error("Unable to fetch all font information");
            });
        },

        /**
         * Register fonts with the internal collection, optionally loading them as well.
         *
         * @param {Object|Object[]} font  The raw font data, as provided by the specific font provider - will be passed to the internal parser.
         * @param {FontProvider} provider The `FontProvider` of the specified font data.
         * @param {Boolean} [load=true] Whether to load the font(s) - defaults to `true`.
         */
        register(font, provider, load = true) {
            const fonts = Array.isArray(font) ? font : [font];
            // Parse the font data to it's raw form.
            const rawFontDescriptors = parsers.get(provider)(fonts);
            // Pass the raw descriptors to the collection.
            this._collection.add(rawFontDescriptors, {at: 0});

            if (load) {
                // We need the actual concrete model instances in order to load the fonts.
                // It seems that future versions of Backbone.Collection#add returns the model instances - that
                // would be ideal, instead of the below lookup...
                const fontDescriptors = rawFontDescriptors.map((rawFontDescriptor) => {
                    const {name, provider} = rawFontDescriptor;
                    return this._collection.findWhere({name, provider});
                });

                FontLoader.fromFontDescriptors(fontDescriptors);
            }
        },

        /**
         * Unregister fonts from the internal collection.
         *
         * @param {Object|Object[]} font  The raw font data, as provided by the specific font provider - will be passed to the internal parser.
         * @param {FontProvider} provider The `FontProvider` of the specified font data.
         */
        unregister(font, provider) {
            const fonts = Array.isArray(font) ? font : [font];
            const rawFontDescriptors = parsers.get(provider)(fonts);
            const fontDescriptors = rawFontDescriptors.map((rawFontDescriptor) => {
                const {name, provider} = rawFontDescriptor;
                return this._collection.findWhere({name, provider});
            });

            this._collection.remove(fontDescriptors);
        },

        /**
         * Clears/removes any previously used font configurations, i.e. removes any fonts from the
         * collection of font descriptors, and any "favoriteFonts" stored in local storage.
         */
        clearFontType(fontProviderId) {
            // If the token was removed, remove the fonts as well
            this._collection.removeByProvider(fontProviderId);
            // Clean up any previously used fonts
            const favorites = Storage.Local.get(Storage.Keys.EDITOR, "favoriteFonts", []);
            // Store all but Typekit
            Storage.Local.update(Storage.Keys.EDITOR, "favoriteFonts", _.filter(favorites, (favorite) => {
                return favorite.fontProvider !== fontProviderId.value;
            }));
        },

        /**
         * @private
         *
         * Tries to fetch all the font information from Typekit given a specific API token.
         *
         * If the request, for some reason, fails, the returned promise will still resolve, but the NotificationBar
         * is invoked with an error message.
         *
         * @param  {String} typekitApi The Typekit API token
         * @return {Promise}                A promise that always resolves, either with an array of kit
         * data, or an empty array.
         */
        _fetchTypekit(typekitApi) {
            if (_.isString(typekitApi)) {
                typekitApi = new TypekitApi(typekitApi);
            }
            logger.info(`Fetching Typekit kit(s), using API token: ${typekitApi}`);
            return Promise.deferred((deferred) => {
                typekitApi.all()
                    .done(deferred.resolve)
                    .fail(() => {
                        logger.error("Unable to communicate with the Typekit API");
                        SystemMessages.ManageFonts.noCommunication("Typekit", "API token");
                        // Just resolve with "empty response"
                        deferred.resolve([]);
                    });
            });
        },

        /**
         * Tries to fetch all the fonts from Fonts.com. The returned promise will never fail. If
         * the request fails a toaster message is show and an empty array is returned.
         *
         * @returns {Promise<Array<FontDescriptor>>}
         */
        _fetchFontsDotCom() {
            logger.info("Fetching Fonts.com project(s).");
            return Promise.deferred((deferred) => {
                FontsDotComApi.all()
                    .done(deferred.resolve)
                    .fail(() => {
                        logger.error("Unable to communicate with Fonts.com.");
                        SystemMessages.ManageFonts.noCommunication("Fonts.com", "authentication key");
                        // Errors are also resolved, but with an empty array.
                        deferred.resolve([]);
                    });
            });
        },

        /**
         * @private
         *
         * Validates the specified Typekit API token, by performing an API request, that if proves
         * successful, resolves the returned promise with a boolean value of `true`, otherwise `false`.
         *
         * @param  {String} typekitApiToken The API token.
         * @return {Promise}                A promise the resolves to either `true` or `false`.
         */
        _validateTypekitToken(typekitApiToken) {
            return Promise.deferred((deferred) => {
                const typekitApi = new TypekitApi(typekitApiToken);
                const failed = () => {
                    deferred.resolve(false);
                    SystemMessages.ManageFonts.invalidKey("Typekit", "API token", typekitApiToken, "typekit:valid:token");
                };

                typekitApi.kits()
                    .done((value) => {
                        if (value && value.errors) {
                            failed();
                        } else {
                            deferred.resolve(true);
                        }
                    })
                    .fail(failed);
            }).promise();
        },

        /**
         * @private
         *
         * Start loading the css of the initial font styles.
         */
        _loadCssOfInitialFonts() {
            // Populate list of fonts to load initially.
            const fonts = this._collection.take(20);

            // We only care about the google fonts since standard fonts
            // does not need to be loaded, and we already load all
            // available Typekit fonts.
            const favoriteGoogleFontDescriptors = this._getFavoriteGoogleFontDescriptors();
            const allGoogleFonts = fonts.get(FontProvider.GOOGLE).concat(favoriteGoogleFontDescriptors);

            fonts.set(FontProvider.GOOGLE, allGoogleFonts);

            // No need to load the "standard" fonts
            fonts.delete(FontProvider.STANDARD);

            const allCustomFonts = this._collection.fromProvider(FontProvider.CUSTOM);
            fonts.delete(FontProvider.CUSTOM);
            fonts.set(FontProvider.CUSTOM, allCustomFonts);

            const queue = SyncManager.queue(true); // parallel

            // Load the initial fonts (including all variants)
            fonts.forEach((fontDescriptors, provider) => {
                queue.enqueue({
                    method: provider.value,
                    fn: () => FontLoader.fromFontDescriptors(fontDescriptors, true),
                    errorHandler() {
                        // suppress error notifications, already handled by font loader
                        return false;
                    }
                }, provider);
            });

            queue.sync();
        },

        /**
         * Gets the favorite fonts belonging to Google and returns an array of
         * fontDescriptors.
         *
         * @private
         * @return {fontDescriptors[]}  Array of favorite google font
         *                              descriptors.
         */
        _getFavoriteGoogleFontDescriptors() {
            const favorites = Storage.Local.get(Storage.Keys.EDITOR, "favoriteFonts", []);
            const filteredFavorites = _.where(favorites, {fontProvider: FontProvider.GOOGLE.value});

            return _.map(filteredFavorites, (favorite) => {
                return this._collection.fromFamily(favorite.fontFamily, FontProvider.GOOGLE);
            });
        }
    });

    // Singleton
    return new EditorWebFontController();
});



// WEBPACK FOOTER //
// ./src/editor/ui/experience/editor/EditorWebFontController.js